import { ChangeEvent, Dispatch, SetStateAction, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as uuid from 'uuid';
import { useQuery } from '@tanstack/react-query';
import {
	CostType,
	CostUnit,
	RehabAction,
	CustomCostAction,
	CostTableAction,
	isCustomCostAction,
	isCostTableAction,
	CostApplication,
} from '@Types';
import {
	useUnits,
	useAdpAnalytics,
	HTMLInputElementExtended,
	HTMLTextAreaElementExtended,
	convertToMetricNoIntlFormat,
	convertToImperialNoIntlFormat,
} from '@innovyze/stylovyze';
import {
	selectRehabActions,
	selectCurrentTree,
} from '@Selectors/Rehab.selectors';
import {
	addNewRehabActionToTree,
	addRehabActionResolved,
	saveRehabActionResolved,
	updateRehabActionInTree,
} from '@Actions/Rehab.actions';
import { useGlobalization } from '@Translations';
import { SelectChangeEvent } from '@mui/material';
import { useAssetTypeHasLength } from '@Components/hooks/useAssetTypeHasLength';
import {
	costTableDetailQuery,
	costTableDataQuery,
} from '../../queries/costTableQueries';
import { defectCodesQuery } from '../../queries/defectCodeQueries';
import { useFilteredCostTables } from '../../queries/useFilteredCostTables';
import type { CostTableSummary } from '@Types/cost.types';
import type { DefectCode } from '@Types/utils.types';
import { InspectionStandard } from '@Types/inspections.types';
import { trackRehabLengthCostActionAdded } from './rehabActionAnalytics';

export type CostSource = 'custom' | 'costTable';

export interface UseRehabActionFormArgs {
	treeId: string;
	rehabAction: RehabAction | undefined | null;
	shouldAddNewActionToTree: () => boolean;
	setIsDirty: Dispatch<SetStateAction<boolean>>;
}

export interface RehabActionFormState {
	// Name
	name: string;
	validName: string;
	onNameChange: (event: ChangeEvent<HTMLInputElement>) => void;

	// Cost source
	costSource: CostSource;
	setCostSource: (source: CostSource) => void;

	// Custom cost
	initialUnitCost: number;
	costUnit: CostUnit;
	validUnitCost: string;
	onUnitCostChange: (
		event: ChangeEvent<HTMLInputElement | HTMLTextAreaElementExtended>,
	) => void;
	onCostUnitChange: (event: SelectChangeEvent<string>) => void;
	onCostPerChange: (value: CostUnit.each | CostApplication) => void;

	// Cost table
	costTables: CostTableSummary[];
	selectedCostTableId: string;
	setSelectedCostTableId: (id: string) => void;
	costTableSummary: string | null;

	// Cost application
	costApplication: CostApplication;
	setCostApplication: (application: CostApplication) => void;

	// Notes
	notes: string;
	onNotesChange: (event: ChangeEvent<HTMLInputElement>) => void;

	// Defect codes
	defectCodes: string[];
	defectCodeOptions: DefectCode[];
	defectCodesLoading: boolean;
	setDefectCodes: (codes: string[]) => void;

	// Derived
	hasLengthProperty: boolean;
	system: string;
	isSaveDisabled: boolean;

	// Actions
	handleAddOrSave: () => boolean;
	resetFromAction: (action: RehabAction | undefined | null) => void;
	clearFields: () => void;
}

export function useRehabActionForm({
	treeId,
	rehabAction,
	shouldAddNewActionToTree,
	setIsDirty,
}: UseRehabActionFormArgs): RehabActionFormState {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const currentTree = selectCurrentTree();
	const hasLengthProperty = useAssetTypeHasLength({
		systemType: currentTree?.systemType,
		assetType: currentTree?.assetType,
	});
	const { system, getSystemUnit } = useUnits();
	const adp = useAdpAnalytics();
	const rehabActions = selectRehabActions();

	// ---- Name ----
	const [validName, setValidName] = useState<string>(
		rehabAction ? '' : 'Enter a valid name',
	);
	const [name, setName] = useState<string>(
		rehabAction ? rehabAction.actionId : '',
	);

	// ---- Cost source ----
	const getInitialCostSource = (): CostSource => {
		if (rehabAction?.costType === CostType.costTable) return 'costTable';
		return 'custom';
	};
	const [costSource, setCostSource] = useState<CostSource>(
		getInitialCostSource(),
	);

	const handleSetCostSource = (source: CostSource) => {
		setCostSource(source);
		if (source !== 'costTable') {
			setCostApplication(CostApplication.pipeLength);
		}
	};

	// ---- Custom cost fields ----
	const [initialUnitCost, setInitialUnitCost] = useState<number>(0);
	const [unitCost, setUnitCost] = useState<number>(0);
	const [unitCostEach, setUnitCostEach] = useState<number>(0);
	const [validUnitCost, setValidUnitCost] = useState<string>('');
	// Cost application
	const [costApplication, setCostApplication] = useState<CostApplication>(
		CostApplication.pipeLength,
	);

	const getDefaultCostUnit = (): CostUnit => {
		if (rehabAction && isCustomCostAction(rehabAction))
			return rehabAction.unit;
		return hasLengthProperty ? CostUnit.length : CostUnit.each;
	};
	const [costUnit, setCostUnit] = useState<CostUnit>(getDefaultCostUnit());

	// ---- Notes ----
	const [notes, setNotes] = useState<string>(
		rehabAction ? rehabAction.notes : '',
	);

	// ---- Defect codes ----
	const [defectCodes, setDefectCodes] = useState<string[]>(
		rehabAction?.settings?.defects ?? [],
	);

	// ---- Cost table selection ----
	const [selectedCostTableId, setSelectedCostTableId] = useState<string>(
		rehabAction && isCostTableAction(rehabAction)
			? rehabAction.costTableId
			: '',
	);

	const costTables = useFilteredCostTables();

	// ---- TanStack Query: cost table detail ----
	const { data: costTableDetail } = useQuery(
		costTableDetailQuery(selectedCostTableId),
	);

	const latestDataId = costTableDetail?.latest_cost_table_data_id ?? '';

	// ---- TanStack Query: cost table data (for summary) ----
	const { data: costTableData = [] } = useQuery(
		costTableDataQuery(selectedCostTableId, latestDataId),
	);

	// ---- TanStack Query: defect codes ----
	const inspectionStandard =
		currentTree?.inspectionStandard as InspectionStandard;
	const { data: defectCodeOptions = [], isLoading: defectCodesLoading } =
		useQuery(defectCodesQuery(inspectionStandard, !!inspectionStandard));

	// ---- Cost table summary ----
	const costTableSummary = (() => {
		if (!costTableDetail || costTableData.length === 0) return null;
		const costs = costTableData
			.map(e => e.unit_cost)
			.filter((c): c is number => c !== null);
		if (costs.length === 0) return null;
		const min = Math.min(...costs);
		const max = Math.max(...costs);
		const costPer = costTableDetail.cost_per;
		let costPerLabel = 'each';
		if (costPer === CostUnit.length) {
			costPerLabel = system === 'Metric' ? '/m (length)' : '/ft (length)';
		}
		return `Unit Cost: $${min.toFixed(2)} - $${max.toFixed(2)}, Cost per: ${costPerLabel}`;
	})();

	// ---- Unit cost helpers ----
	const getUnitCostEach = (unit: CostUnit, value: number) => {
		if (unit === CostUnit.length && system === 'Imperial') {
			const result = convertToImperialNoIntlFormat(`${value} cost/m`);
			return +result.value;
		}
		return value;
	};

	// ---- Field change handlers ----
	const onNameChange = (event: ChangeEvent<HTMLInputElement>) => {
		const sValue = event.target.value;
		let error = '';
		if (sValue.trim().length === 0) {
			error = t('Enter a valid name');
		} else {
			const rexp = /^[a-zA-Z0-9 ._@-]*$/;
			if (!rexp.test(sValue)) {
				error = t(
					'Only letters, numbers, spaces and some special characters (._@-) are allowed',
				);
			} else if (sValue !== rehabAction?.actionId) {
				const duplicate = rehabActions?.find(
					a => a.actionId.toUpperCase() === sValue.toUpperCase(),
				);
				if (duplicate) error = t('Enter a unique name');
			}
		}
		setValidName(error);
		setName(sValue);
	};

	const onUnitCostChange = (
		event: ChangeEvent<
			| HTMLInputElement
			| HTMLInputElementExtended
			| HTMLTextAreaElementExtended
		>,
	) => {
		const actualEntry = Number.parseFloat(event.target.value);
		let error = '';
		if (Number.isNaN(actualEntry)) {
			error = t('Enter a unit cost');
		} else if (actualEntry < 0) {
			error = t('Unit cost must be 0 or greater');
		}
		const dValue = Number(event.target.value);
		setValidUnitCost(error);
		setUnitCost(error.length === 0 ? dValue : -1);
		setUnitCostEach(getUnitCostEach(costUnit, dValue));
	};

	const onCostUnitChange = (event: SelectChangeEvent<string>) => {
		const target = event?.target as HTMLInputElement;
		const newUnit = target?.value as CostUnit;
		if (newUnit === CostUnit.length) {
			const unit = getSystemUnit('cost/m');
			const value = convertToMetricNoIntlFormat(`${unitCost} ${unit}`);
			setUnitCostEach(unitCost);
			setUnitCost(+value.value);
			setInitialUnitCost(+value.value);
		} else {
			setUnitCost(unitCostEach);
			setInitialUnitCost(unitCostEach);
		}
		setCostUnit(target?.value as CostUnit);
	};

	// Merges the Cost Unit (each/length) and Cost Application (pipe/rehab
	// length) axes into the single "Cost per" control custom-cost actions
	// present to the user, while keeping them as separate pieces of state
	// internally (unchanged data model, shared with cost-table actions).
	const onCostPerChange = (value: CostUnit.each | CostApplication) => {
		const unit = value === CostUnit.each ? CostUnit.each : CostUnit.length;
		onCostUnitChange({
			target: { value: unit },
		} as SelectChangeEvent<string>);
		setCostApplication(
			value === CostUnit.each ? CostApplication.pipeLength : value,
		);
	};

	const onNotesChange = (event: ChangeEvent<HTMLInputElement>) => {
		setNotes(event.target.value);
	};

	// ---- Clear / reset ----
	const clearFields = () => {
		setName('');
		setValidName(t('Enter a valid name'));
		setInitialUnitCost(0);
		setUnitCost(0);
		setUnitCostEach(0);
		setValidUnitCost('');
		setCostUnit(hasLengthProperty ? CostUnit.length : CostUnit.each);
		setNotes('');
		setDefectCodes([]);
		setCostSource('custom');
		setSelectedCostTableId('');
		setCostApplication(CostApplication.pipeLength);
	};

	const resetFromAction = (action: RehabAction | undefined | null) => {
		if (action) {
			setName(action.actionId);
			setValidName('');
			setNotes(action.notes);
			setDefectCodes(action.settings?.defects ?? []);
			if (isCustomCostAction(action)) {
				setCostSource('custom');
				setSelectedCostTableId('');
				setInitialUnitCost(action.unitCost);
				setUnitCost(action.unitCost);
				setUnitCostEach(getUnitCostEach(action.unit, action.unitCost));
				setCostUnit(action.unit);
				setCostApplication(
					action.costApplication || CostApplication.pipeLength,
				);
			} else {
				setCostSource('costTable');
				setSelectedCostTableId(action.costTableId);
				setCostApplication(
					action.costApplication || CostApplication.pipeLength,
				);
			}
		} else {
			clearFields();
		}
	};

	// ---- Save ----
	const isSaveDisabled = (() => {
		if (name.trim() === '' || validName !== '') return true;
		if (costSource === 'custom')
			return validUnitCost !== '' || unitCost < 0;
		return !selectedCostTableId;
	})();

	const handleAddOrSave = (): boolean => {
		if (name === '') return false;

		const buildAction = (): RehabAction => {
			const base = {
				_id: rehabAction?._id || uuid.v4(),
				actionId: name,
				treeId,
				settings: {
					enabled: true,
					defects: defectCodes,
				},
				notes,
			};

			if (
				costSource === 'costTable' &&
				selectedCostTableId &&
				costTableDetail
			) {
				const action: CostTableAction = {
					...base,
					costType: CostType.costTable,
					costTableId: selectedCostTableId,
					costTableDataId: latestDataId,
					costApplication,
				};
				return action;
			}

			// "Apply to" only applies (and is only rendered) when the cost is
			// per length; guard against a stale trenchLength/pipeLength value
			// surviving a switch back to a per-each cost unit.
			const action: CustomCostAction = {
				...base,
				unitCost,
				unit: costUnit,
				costType: CostType.custom,
				costApplication:
					costUnit === CostUnit.length
						? costApplication
						: CostApplication.pipeLength,
			};
			return action;
		};

		if (unitCost < 0 && costSource === 'custom') return false;

		const action = buildAction();

		if (rehabAction) {
			dispatch(saveRehabActionResolved(action));
			dispatch(updateRehabActionInTree(action));
		} else {
			dispatch(addRehabActionResolved(action));
			if (shouldAddNewActionToTree()) {
				dispatch(addNewRehabActionToTree(action));
			}
			if (action.costApplication === CostApplication.trenchLength) {
				try {
					trackRehabLengthCostActionAdded(adp, {
						treeId,
						systemType: currentTree?.systemType?.id ?? '',
						inspectionStandard:
							currentTree?.inspectionStandard ?? '',
						assetType: currentTree?.assetType?.id ?? '',
						costSource: isCostTableAction(action)
							? 'costTable'
							: 'custom',
					});
				} catch (error) {
					console.error(
						'Error tracking rehab-length cost action added',
						error,
					);
				}
			}
		}
		setIsDirty(true);
		return true;
	};

	return {
		name,
		validName,
		onNameChange,
		costSource,
		setCostSource: handleSetCostSource,
		initialUnitCost,
		costUnit,
		validUnitCost,
		onUnitCostChange,
		onCostUnitChange,
		onCostPerChange,
		costTables,
		selectedCostTableId,
		setSelectedCostTableId,
		costTableSummary,
		notes,
		onNotesChange,
		defectCodes: defectCodes,
		defectCodeOptions,
		defectCodesLoading,
		setDefectCodes,
		hasLengthProperty,
		system,
		isSaveDisabled,
		handleAddOrSave,
		resetFromAction,
		clearFields,
		costApplication,
		setCostApplication,
	};
}
